import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { NotFoundState } from '@/components/common/NotFoundState';
import { useLang } from '@/i18n/useLocalizedPath';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3, Download, ChevronRight, ArrowLeft, Loader2,
  MapPin, Building, Calendar, TrendingUp, Share2, Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InsightReportPage() {
  const { slug } = useParams();
  const lang = useLang();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  
  // Download form
  const [downloadForm, setDownloadForm] = useState({ email: '', name: '' });
  const [downloading, setDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [slug, lang]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/reports/${slug}?lang=${lang}`);
      setReport(response.data.report);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError('Failed to load report');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!downloadForm.email) return;
    
    setDownloading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/public/reports/${slug}/download`,
        downloadForm
      );
      
      setDownloadSuccess(true);
      setPdfUrl(`${BACKEND_URL}${response.data.pdfUrl}`);
    } catch (err) {
      console.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: report.title,
        text: report.seo?.metaDescription,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </MainLayout>
    );
  }

  if (notFound) {
    return (
      <MainLayout>
        <NotFoundState
          title="Report not found"
          primaryTo="/insights"
          primaryLabel="Browse insights"
        />
      </MainLayout>
    );
  }

  if (error || !report) {
    return (
      <MainLayout>
        <div className="text-center py-24 px-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">We couldn't load this report</h1>
          <Link to="/insights">
            <Button data-testid="insight-report-fallback-button">View All Reports</Button>
          </Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEOHead
        title={report.seo?.metaTitle || report.title}
        description={report.seo?.metaDescription}
        canonicalUrl={`/insights/${slug}`}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-purple-900 to-purple-800 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/insights" className="inline-flex items-center text-purple-300 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" /> All Insights
          </Link>
          
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-6 h-6 text-purple-300" />
            <span className="text-purple-300 font-medium">JIGSAW INSIGHTS</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            {report.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-purple-200">
            <Badge className="bg-purple-700 text-white">{report.sector}</Badge>
            <span>{report.period}</span>
            <span>·</span>
            <span>{report.data?.totalEvents} events analyzed</span>
            <span>·</span>
            <span>Published {new Date(report.publishedAt).toLocaleDateString()}</span>
          </div>
          
          <div className="flex gap-3 mt-6">
            <a href="#download">
              <Button className="bg-white text-purple-900 hover:bg-gray-100">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </a>
            <Button variant="outline" className="border-white text-white hover:bg-white/10" onClick={handleShare}>
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            {/* Executive Summary */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Executive Summary</h2>
              <p className="text-gray-700 leading-relaxed">
                {report.content?.executiveSummary}
              </p>
            </div>

            {/* Key Trends */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Trends</h2>
              <div className="space-y-6">
                {report.content?.keyTrends?.map((trend, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {index + 1}. {trend.title}
                      </h3>
                      <p className="text-gray-600">{trend.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Data Visualization */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Data Breakdown</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* By City */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <MapPin className="w-5 h-5 mr-2 text-purple-600" />
                      Events by City
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {report.data?.byCity?.slice(0, 5).map((city, i) => {
                        const maxCount = report.data.byCity[0]?.count || 1;
                        const percentage = (city.count / maxCount) * 100;
                        return (
                          <div key={i}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium">{city._id}</span>
                              <span className="text-gray-500">{city.count} events</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-purple-600 rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* By Venue */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Building className="w-5 h-5 mr-2 text-purple-600" />
                      Top Venues
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {report.data?.byVenue?.slice(0, 5).map((venue, i) => {
                        const maxCount = report.data.byVenue[0]?.count || 1;
                        const percentage = (venue.count / maxCount) * 100;
                        return (
                          <div key={i}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium truncate">{venue.venueName}</span>
                              <span className="text-gray-500 ml-2">{venue.count}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-orange-500 rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Analysis Sections */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Regional Analysis</h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                {report.content?.cityAnalysis}
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Organiser Landscape</h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                {report.content?.organiserLandscape}
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Forecast</h2>
              <p className="text-gray-700 leading-relaxed">
                {report.content?.forecast}
              </p>
            </div>

            {/* Featured Events */}
            {report.data?.featuredEvents?.length > 0 && (
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Events</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {report.data.featuredEvents.map((event, i) => (
                    <Link
                      key={i}
                      to={`/events/${event.citySlug}/${event.venueSlug}/${event.slug}`}
                      className="block p-4 border rounded-lg hover:border-purple-300 hover:shadow-md transition-all"
                    >
                      <h4 className="font-semibold text-gray-900 mb-2">{event.name}</h4>
                      <div className="flex items-center text-sm text-gray-500 mb-1">
                        <Building className="w-4 h-4 mr-1" />
                        {event.venueName}, {event.city}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {event.dates?.display || 'Dates TBC'}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Download CTA */}
          <div id="download" className="mt-12 scroll-mt-24">
            <Card className="bg-gradient-to-r from-purple-50 to-orange-50 border-purple-200">
              <CardContent className="py-8">
                <div className="max-w-md mx-auto text-center">
                  <Download className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Download Full PDF Report
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Get the complete report with additional data, charts, and methodology notes.
                  </p>
                  
                  {downloadSuccess ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center text-green-600 font-medium">
                        <Check className="w-5 h-5 mr-2" /> Your download is ready!
                      </div>
                      <a href={pdfUrl} target="_blank" rel="noreferrer">
                        <Button className="bg-purple-600 hover:bg-purple-700">
                          <Download className="w-4 h-4 mr-2" /> Download PDF
                        </Button>
                      </a>
                    </div>
                  ) : (
                    <form onSubmit={handleDownload} className="space-y-3">
                      <Input
                        type="email"
                        placeholder="Your email *"
                        value={downloadForm.email}
                        onChange={(e) => setDownloadForm({ ...downloadForm, email: e.target.value })}
                        required
                      />
                      <Input
                        type="text"
                        placeholder="Your name (optional)"
                        value={downloadForm.name}
                        onChange={(e) => setDownloadForm({ ...downloadForm, name: e.target.value })}
                      />
                      <Button type="submit" disabled={downloading} className="w-full">
                        {downloading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <><Download className="w-4 h-4 mr-2" /> Download PDF</>
                        )}
                      </Button>
                      <p className="text-xs text-gray-500">
                        By downloading, you agree to receive quarterly insights from Jigsaw Conferences.
                      </p>
                    </form>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Methodology */}
          <div className="mt-8 pt-8 border-t text-sm text-gray-500">
            <h4 className="font-semibold text-gray-700 mb-2">Methodology</h4>
            <p>{report.content?.methodology}</p>
          </div>
        </div>
      </section>
    </MainLayout>
  );
}
